import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { matchKeywordCount } from './questionScoring';

const ACTION_OPTIONS = [
  { value: 'continue', label: 'Continue', variant: 'success' },
  { value: 'change', label: 'Change', variant: 'warning' },
  { value: 'stop', label: 'Stop', variant: 'danger' },
  { value: 'refer', label: 'Refer', variant: 'info' },
];

// Find a prescription on the chart whose drug field matches this rxReview.
// We do a relaxed substring match so "Aspirin" in the question matches
// "Aspirin 75mg OD" on the chart.
const findChartEntry = (chartList, drugName) => {
  if (!Array.isArray(chartList) || !drugName) return null;
  const needle = String(drugName).toLowerCase().trim();
  return chartList.find((rx) => {
    const drug = String(rx?.drug || '').toLowerCase().trim();
    return drug === needle || drug.startsWith(needle) || needle.startsWith(drug);
  }) || null;
};

const ChartReview = ({ question, prescriptions = [] }) => {
  const rxReviews = useMemo(() => Array.isArray(question?.rxReviews) ? question.rxReviews : [], [question]);
  const [responses, setResponses] = useState(() => rxReviews.map(() => ({ action: '', rationale: '' })));
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = !submitted && responses.every((r) => r.action);

  const handleAction = (idx, value) => {
    setResponses((curr) => curr.map((r, i) => (i === idx ? { ...r, action: value } : r)));
  };

  const handleRationale = (idx, value) => {
    setResponses((curr) => curr.map((r, i) => (i === idx ? { ...r, rationale: value } : r)));
  };

  const results = useMemo(() => {
    if (!submitted) return null;
    return rxReviews.map((expected, idx) => {
      const submittedRow = responses[idx] || { action: '', rationale: '' };
      const actionCorrect = String(submittedRow.action || '').toLowerCase() === String(expected.expectedAction || '').toLowerCase();
      const rationaleHits = matchKeywordCount(submittedRow.rationale, expected.rationaleKeywords);
      const rationaleTarget = (expected.rationaleKeywords || []).length;
      // Pass rationale if at least half the keywords are present (or none required)
      const rationaleCorrect = rationaleTarget === 0 ? true : rationaleHits >= Math.ceil(rationaleTarget / 2);
      return {
        actionCorrect,
        rationaleCorrect,
        bothCorrect: actionCorrect && rationaleCorrect,
        rationaleHits,
        rationaleTarget,
      };
    });
  }, [submitted, rxReviews, responses]);

  const overallCorrect = results ? results.every((r) => r.bothCorrect) : false;
  const correctCount = results ? results.filter((r) => r.bothCorrect).length : 0;

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded chart-review-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {submitted ? (
          overallCorrect
            ? <Badge bg="success">All correct</Badge>
            : <Badge bg="warning" text="dark">{correctCount} / {rxReviews.length} fully correct</Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-3">{question.questionText}</p>

      {rxReviews.length === 0 ? (
        <p className="text-muted">No prescription rows configured for this review.</p>
      ) : (
        <div className="chart-review-question__rows">
          {rxReviews.map((rx, idx) => {
            const submittedRow = responses[idx] || { action: '', rationale: '' };
            const result = results ? results[idx] : null;
            const chartEntry = findChartEntry(prescriptions, rx.drug);
            return (
              <div key={`${rx.drug}-${idx}`} className="chart-review-question__row">
                <div className="chart-review-question__rx">
                  <strong>{rx.drug}</strong>
                  {chartEntry?.dose ? <span className="text-muted ms-2">{chartEntry.dose} · {chartEntry.frequency}</span> : null}
                  {chartEntry?.indication ? <div className="small text-muted">Indication: {chartEntry.indication}</div> : null}
                </div>
                <div className="chart-review-question__actions">
                  {ACTION_OPTIONS.map((opt) => {
                    const isSelected = submittedRow.action === opt.value;
                    const isExpected = submitted && String(rx.expectedAction || '').toLowerCase() === opt.value;
                    let variant = isSelected ? opt.variant : `outline-${opt.variant}`;
                    if (submitted) {
                      if (isExpected) variant = opt.variant; // highlight the right answer
                      if (isSelected && !isExpected) variant = 'outline-danger';
                    }
                    return (
                      <Button
                        key={opt.value}
                        type="button"
                        size="sm"
                        variant={variant}
                        disabled={submitted}
                        onClick={() => handleAction(idx, opt.value)}
                      >
                        {opt.label}{submitted && isExpected ? ' ✓' : ''}
                      </Button>
                    );
                  })}
                </div>
                <Form.Control
                  as="textarea"
                  rows={2}
                  className="chart-review-question__rationale"
                  placeholder="Rationale: why this action?"
                  value={submittedRow.rationale}
                  disabled={submitted}
                  onChange={(e) => handleRationale(idx, e.target.value)}
                />
                {submitted ? (
                  <div className="chart-review-question__feedback">
                    <Badge bg={result.actionCorrect ? 'success' : 'danger'} className="me-2">
                      Action {result.actionCorrect ? 'correct' : 'incorrect'}
                    </Badge>
                    {result.rationaleTarget > 0 ? (
                      <Badge bg={result.rationaleCorrect ? 'success' : 'warning'} text={result.rationaleCorrect ? 'light' : 'dark'}>
                        Rationale {result.rationaleHits}/{result.rationaleTarget} key points
                      </Badge>
                    ) : null}
                    {!result.actionCorrect && rx.modelExplanation ? (
                      <div className="small text-muted mt-1"><strong>Model:</strong> {rx.modelExplanation}</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {!submitted ? (
        <Button
          type="button"
          variant="primary"
          className="mt-3"
          disabled={!canSubmit}
          onClick={() => setSubmitted(true)}
        >
          Submit review
        </Button>
      ) : null}
    </Container>
  );
};

export default ChartReview;
